'use client';

import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { m, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

const ROWS = [
  { label: 'Intake', detail: 'Payer requirements pulled for 95810', time: '9:02' },
  { label: 'Letter', detail: 'Medical necessity drafted, physician signed', time: '9:06' },
  { label: 'Submitted', detail: 'Form filled and checked, 0 fields missing', time: '9:07' },
  { label: 'Approved', detail: 'Authorization number issued by the plan', time: 'Day 2' },
];

const STEP_DELAYS = [900, 1700, 2500, 3400];

/**
 * The hero artifact: a prior authorization case file that completes itself.
 * Plays once on load. Reduced motion renders the finished state.
 */
export function CaseFile({ payer, delay = 0 }: { payer: string; delay?: number }) {
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (reduce) {
      setStage(ROWS.length);
      return;
    }
    const timers = STEP_DELAYS.map((ms, i) =>
      window.setTimeout(() => setStage(i + 1), ms + delay * 1000),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [reduce, delay]);

  const approved = stage >= ROWS.length;

  return (
    <m.figure
      initial={{ y: 24 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
      className="relative w-full max-w-[30rem] text-ink-900"
      aria-label="A prior authorization case moving from intake to approval"
    >
      <div className="rounded-[1.25rem] bg-white shadow-[0_40px_90px_-30px_rgba(59,102,245,0.55)]">
        <div className="rule-ink flex items-baseline justify-between border-b px-5 py-4">
          <div>
            <p className="text-[0.8rem] font-medium text-muted">Case 0142</p>
            <p className="font-display mt-0.5 text-[1.05rem] leading-tight font-bold tracking-[-0.01em]">
              Attended polysomnography
            </p>
          </div>
          <dl className="text-right text-[0.8rem]">
            <div className="flex justify-end gap-2">
              <dt className="text-muted">CPT</dt>
              <dd className="tabular font-semibold">95810</dd>
            </div>
            <div className="flex justify-end gap-2">
              <dt className="text-muted">Plan</dt>
              <dd className="font-semibold">{payer}</dd>
            </div>
          </dl>
        </div>

        <ol className="relative px-5 py-3">
          <div aria-hidden="true" className="absolute top-6 bottom-6 left-[1.9rem] w-px bg-ink-900/12" />
          <m.div
            aria-hidden="true"
            className="absolute top-6 left-[1.9rem] w-px origin-top bg-beacon-500"
            style={{ height: 'calc(100% - 3rem)' }}
            initial={false}
            animate={{ scaleY: Math.max(0, (stage - 1) / (ROWS.length - 1)) }}
            transition={{ duration: reduce ? 0 : 0.6, ease: [0.16, 1, 0.3, 1] }}
          />
          {ROWS.map((row, i) => {
            const done = stage > i;
            const active = stage === i + 1 && !approved;
            return (
              <li key={row.label} className="relative flex items-start gap-4 py-3">
                <span
                  className={clsx(
                    'relative z-1 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
                    done
                      ? i === ROWS.length - 1
                        ? 'border-verdant bg-verdant text-white'
                        : 'border-beacon-500 bg-beacon-500 text-white'
                      : 'border-ink-900/25 bg-white',
                  )}
                >
                  {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p
                      className={clsx(
                        'text-[0.95rem] font-semibold transition-colors duration-300',
                        done ? 'text-ink-900' : 'text-ink-900/60',
                        active && 'text-beacon-600',
                      )}
                    >
                      {row.label}
                    </p>
                    <p
                      className={clsx(
                        'tabular text-[0.8rem] transition-opacity duration-300',
                        done ? 'text-muted opacity-100' : 'opacity-0',
                      )}
                    >
                      {row.time}
                    </p>
                  </div>
                  <p
                    className={clsx(
                      'text-[0.85rem] leading-snug transition-colors duration-300',
                      done ? 'text-muted' : 'text-ink-900/50',
                    )}
                  >
                    {row.detail}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="rule-ink flex items-center justify-between border-t px-5 py-3 text-[0.8rem] text-muted">
          <span>Denial risk</span>
          <span className={clsx('font-semibold', approved ? 'text-verdant' : 'text-ink-900')}>
            {approved ? 'Cleared' : stage >= 1 ? '1 requirement open' : 'Checking'}
          </span>
        </div>
      </div>

      <m.div
        aria-hidden="true"
        initial={false}
        animate={
          approved ? { opacity: 1, scale: 1, rotate: -8 } : { opacity: 0, scale: 1.5, rotate: -8 }
        }
        transition={{ duration: reduce ? 0 : 0.35, ease: [0.34, 1.56, 0.64, 1] }}
        className="font-display pointer-events-none absolute -top-8 -right-3 rounded-md border-[3px] border-verdant bg-white/85 px-3 py-1 text-[1.4rem] leading-none font-extrabold tracking-[0.06em] text-verdant uppercase sm:-top-7 sm:-right-7"
      >
        Approved
      </m.div>
    </m.figure>
  );
}
